import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useContext, useEffect, useState } from 'react'
import useWebSocket, { ReadyState } from 'react-use-websocket'
import useProcessMessage, { UnprocessedMessage } from '../hooks/azurewebsocket/useProcessMessage'
import AuthorizationContext, { SecurityAuthority } from './AuthorizationContext'

/**
 * Defines all the different types of messages the client might receive.
 */
export enum MessageType {
    System = 'system',
    Message = 'message',
}

/**
 * Defines all the different types of system messages the client might receive.
 */
export enum SystemMessageType {
    Connected = 'connected',
    Disconnected = 'disconnected',
}

/**
 * All messages (and their data) have at least a type.
 */
export type WebSocketMessage<PayloadT> = PayloadT & {
    type: MessageType
}

type SystemMessage<ParamsT> = WebSocketMessage<
    ParamsT & {
        event: string
    }
>

/**
 * Connect message from Azure Web PubSub.
 */
type SystemConnectMessage = SystemMessage<{
    userId: string
    connectionId: string
    reconnectionToken: string
}>

/**
 * Disconnect message from Azure Web PubSub.
 */
type SystemDisconnectMessage = SystemMessage<{
    message: string
}>

export type AzureWebSocketType = {
    hostId: string
}

const AzureWebSocketContext = createContext<AzureWebSocketType>({
    hostId: undefined,
})

/**
 * Process a system connected message.
 * When connecting the message contains a connectionId and a reconnectionToken. These can be used later on to reconnect
 * the websocket and resume state and messages. These variables are only provided upon connect. The client should
 * persist these variables to be used upon reconnect.
 * @param messageDetails The details of the system message.
 * @param setConnectionId The method to set the ID of the current websocket connection.
 * @param setReconnectionToken The method to set a reconnection token, which can be used to resume the current connection.
 */
function processConnectedMessage(
    messageDetails: SystemConnectMessage,
    setConnectionId: (value: ((prevState: string) => string) | string) => void,
    setReconnectionToken: (value: ((prevState: string) => string) | string) => void,
): void {
    setConnectionId(messageDetails.connectionId)
    setReconnectionToken(messageDetails.reconnectionToken)
}

/**
 * Process a system disconnected message.
 * Disconnect first, then Assign new a socketUrl, which will trigger a reconnect.
 * @param _messageDetails The details of the system message
 * @param connectionId The ID of the current websocket connection, can be used to reconnect and resume state.
 * @param reconnectionToken A token which can be used to resume the connection and resume state.
 * @param socketUrl The current socketUrl value.
 * @param setSocketUrl The method to set a new socketUrl.
 */
function processDisconnectedMessage(
    _messageDetails: SystemDisconnectMessage,
    connectionId: string,
    reconnectionToken: string,
    socketUrl: string,
    setSocketUrl: (value: ((prevState: string) => string) | string) => void,
): void {
    setSocketUrl(null)
    const url = new URL(socketUrl)
    const newSocketUrl = `${url.origin}${url.pathname}?awps_connection_id=${connectionId}&awps_reconnection_token=${reconnectionToken}`
    setSocketUrl(newSocketUrl)
}

export function AzureWebSocketProvider(props: PropsWithChildren): ReactElement {
    const { host } = useContext(AuthorizationContext)
    const processMessage = useProcessMessage()

    const { tokens, tokensRevision } = useContext(AuthorizationContext)

    const [socketUrl, setSocketUrl] = useState<string>(null)
    const [connectionId, setConnectionId] = useState<string>(null)
    const [reconnectionToken, setReconnectionToken] = useState<string>(null)
    const { lastMessage: messageEvent, readyState } = useWebSocket(socketUrl, {
        protocols: ['json.reliable.webpubsub.azure.v1'],
        retryOnError: true,
        reconnectAttempts: 2,
        share: true,
    })

    useEffect(() => {
        if (tokens?.[SecurityAuthority.WebPubSub]) {
            setSocketUrl(tokens?.[SecurityAuthority.WebPubSub]?.url)
        }
    }, [tokensRevision])

    useEffect(() => {
        if (readyState !== null && readyState === ReadyState.CLOSED) {
            setConnectionId(null)
            setReconnectionToken(null)
        }
    }, [readyState])

    useEffect(() => {
        if (!messageEvent?.data) {
            return
        }

        const message: WebSocketMessage<unknown> = JSON.parse(messageEvent.data)
        switch (message.type) {
            case MessageType.System: {
                switch ((message as SystemMessage<unknown>)?.event) {
                    case SystemMessageType.Connected:
                        processConnectedMessage(message as SystemConnectMessage, setConnectionId, setReconnectionToken)
                        break
                    case SystemMessageType.Disconnected:
                        processDisconnectedMessage(message as SystemDisconnectMessage, connectionId, reconnectionToken, socketUrl, setSocketUrl)
                        break
                }
                break
            }
            case MessageType.Message:
                // At this stage we don't know the contents of the payload yet, therefore default to the unknown type.
                processMessage(message as UnprocessedMessage)
                break
        }
    }, [messageEvent])

    return <AzureWebSocketContext.Provider value={{ hostId: host?.RowKey }}>{props.children}</AzureWebSocketContext.Provider>
}

// noinspection JSUnusedGlobalSymbols
export default AzureWebSocketContext
